/**
 * DataSourceCatalog — Data catalog entries with quality scores, scan trigger
 * Phase 4.8-B — WQ-198
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';
import { listCatalogEntries, getCatalogStats, triggerScan, getEntrySchema, type CatalogEntry, type CatalogStats } from '../../services/catalogService';

const QUALITY_COLORS: Record<string, string> = {
  high: '#22c55e', medium: '#eab308', low: '#f97316', unknown: '#6b7280',
};

function qualityLevel(score: number | null): string {
  if (score === null) return 'unknown';
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

const DataSourceCatalog: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [schemaDrawer, setSchemaDrawer] = useState<{ id: string; data: Record<string, unknown> } | null>(null);
  const [scanning, setScanning] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};
      if (sourceTypeFilter) filters.source_type = sourceTypeFilter;
      const [data, statsData] = await Promise.all([
        listCatalogEntries(authFetch, filters),
        getCatalogStats(authFetch).catch(() => null),
      ]);
      setEntries(Array.isArray(data) ? data : []);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load catalog');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading, sourceTypeFilter]);

  const handleScan = async () => {
    setScanning(true);
    try {
      await triggerScan(authFetch, {});
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
    }
    setScanning(false);
  };

  const handleViewSchema = async (id: string) => {
    try {
      const data = await getEntrySchema(authFetch, id);
      setSchemaDrawer({ id, data });
    } catch {
      setSchemaDrawer({ id, data: { error: 'Failed to load schema' } });
    }
  };

  const filtered = entries.filter(e =>
    !searchQuery ||
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.source_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const uniqueSourceTypes = [...new Set(entries.map(e => e.source_type).filter(Boolean))].sort();

  if (loading && entries.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading data catalog...</div>;
  }

  return (
    <div>
      {/* Stats bar */}
      {stats && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Sources', value: stats.total },
            { label: 'Active', value: stats.active },
            { label: 'Avg Quality', value: stats.avg_quality_score !== null ? `${(stats.avg_quality_score * 100).toFixed(0)}%` : '—' },
          ].map(s => (
            <div key={s.label} style={{
              padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-secondary)', minWidth: 100,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder="Search data sources..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <select
          value={sourceTypeFilter} onChange={e => setSourceTypeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Types</option>
          {uniqueSourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={handleScan} disabled={scanning} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          opacity: scanning ? 0.6 : 1,
        }}>
          {scanning ? 'Scanning...' : 'Trigger Scan'}
        </button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {searchQuery ? 'No data sources match your search.' : 'No data sources registered.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Source Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Quality</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>AutoML</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Scan</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => {
                const ql = qualityLevel(entry.quality_score);
                return (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{entry.name}</td>
                    <td style={{ padding: '8px 12px' }}>{entry.source_type}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: `${QUALITY_COLORS[ql]}20`, color: QUALITY_COLORS[ql],
                      }}>
                        {entry.quality_score !== null ? `${(entry.quality_score * 100).toFixed(0)}%` : '—'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: entry.automl_eligible ? '#dcfce7' : '#f3f4f6',
                        color: entry.automl_eligible ? '#166534' : '#6b7280',
                      }}>
                        {entry.automl_eligible ? 'Eligible' : 'No'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12 }}>
                      {entry.scan_status || '—'}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: entry.is_active ? '#dcfce7' : '#fef2f2',
                        color: entry.is_active ? '#166534' : '#dc2626',
                      }}>
                        {entry.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                      <button
                        onClick={() => handleViewSchema(entry.id)}
                        style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 4,
                          border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer',
                        }}
                      >
                        Schema
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Schema drawer */}
      {schemaDrawer && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
          background: 'var(--bg-primary)', borderLeft: '1px solid var(--border)',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.1)', zIndex: 100, padding: 20, overflow: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 14 }}>Schema Preview</strong>
            <button onClick={() => setSchemaDrawer(null)} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 18,
            }}>&times;</button>
          </div>
          <pre style={{ fontSize: 11, overflow: 'auto', background: 'var(--bg-secondary)', padding: 12, borderRadius: 6 }}>
            {JSON.stringify(schemaDrawer.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default DataSourceCatalog;
